import { makeStyles } from 'tss-react/mui';

type StylesProps = {
  hideForLater: boolean;
};

const useStyles = makeStyles<StylesProps>()((theme, { hideForLater }) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(8),
    width: '100%',
    [theme.breakpoints.down('md')]: {
      flex: 1,
      minHeight: 0,
      gap: 0,
      paddingBottom: hideForLater ? theme.spacing(2) : 0,
    },
  },
  scrollArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(8),
    [theme.breakpoints.down('md')]: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      gap: theme.spacing(6),
    },
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    width: '100%',
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  sectionSubtitle: {
    color: theme.palette.text.secondary,
  },
  keyLink: {
    font: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    letterSpacing: 'inherit',
    verticalAlign: 'baseline',
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    textTransform: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
  answerPaper: {
    width: '100%',
    maxWidth: 800,
    [theme.breakpoints.down('md')]: {
      '&&.MuiDialog-paper': {
        margin: 0,
        width: '100vw',
        maxWidth: '100vw',
        maxHeight: '100dvh',
        borderRadius: 0,
      },
    },
  },
  answerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4, 5, 3, 5),
    borderBottom: `thin solid ${theme.palette.grey[400]}`,
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 3),
    },
  },
  answerTitle: {
    fontWeight: 700,
  },
  answerContent: {
    padding: theme.spacing(4, 5, 5, 5),
    overflowY: 'auto',
    maxHeight: 'calc(100dvh - 8rem)',
    [theme.breakpoints.down('md')]: {
      padding: 0,
      maxHeight: 'calc(100dvh - 48px)',
    },
  },
  answerEmpty: {
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(4, 5),
    },
  },
  answerImageFrame: {
    width: '100%',
    minHeight: 160,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundColor: theme.palette.common.white,
    '& > *': {
      width: '100%',
    },
    '& .react-transform-wrapper, & .react-transform-component': {
      width: '100%',
    },
    '& *': {
      border: 'none',
    },
    '&& img': {
      margin: 0,
      width: '100%',
      display: 'block',
    },
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  footer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    width: '100%',
    marginTop: theme.spacing(2),
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(2),
    },
  },
  footerButton: {
    minWidth: 88,
    minHeight: 35,
  },
  hideForLater: {
    maxWidth: '340px',
    alignSelf: 'flex-start',
    [theme.breakpoints.down('md')]: {
      alignSelf: 'center',
    },
  },
  checkboxLabel: {
    fontSize: '.875rem',
    userSelect: 'none',
  },
}));

type ButtonStylesProps = {
  color?: string;
};

const useButtonStyles = makeStyles<ButtonStylesProps>()((theme, { color }) => ({
  button: {
    flex: 1,
    justifyContent: 'flex-start',
    textAlign: 'left',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    minHeight: 64,
    padding: theme.spacing(4, 5),
    borderRadius: 10,
    borderWidth: 2,
    borderColor: theme.palette.grey[400],
    color: theme.palette.common.black,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
      borderWidth: 2,
      borderColor: color ?? theme.palette.primary.main,
      color: color ?? theme.palette.common.black,
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(2.5),
    },
    '& .MuiButton-startIcon > *:nth-of-type(1)': {
      fontSize: 32,
    },
    [theme.breakpoints.down('md')]: {
      minHeight: 44,
      padding: theme.spacing(2, 3),
      fontSize: '.875rem',
      borderRadius: 8,
      '& .MuiButton-startIcon': {
        marginRight: theme.spacing(1.5),
      },
      '& .MuiButton-startIcon > *:nth-of-type(1)': {
        fontSize: 22,
      },
    },
  },
  buttonSelected: {
    // Double specificity so MUI outlined styles do not keep grey border / tinted bg
    '&&': {
      borderColor: color ?? theme.palette.primary.main,
      borderWidth: 2,
      color: color ?? theme.palette.primary.main,
      backgroundColor: theme.palette.common.white,
      '&:hover': {
        borderWidth: 2,
        borderColor: color ?? theme.palette.primary.main,
        color: color ?? theme.palette.primary.main,
        backgroundColor: theme.palette.common.white,
      },
    },
  },
  iconImage: {
    width: 32,
    height: 32,
    objectFit: 'contain',
    display: 'block',
    [theme.breakpoints.down('md')]: {
      width: 22,
      height: 22,
    },
  },
  icon: {
    fontSize: 32,
    [theme.breakpoints.down('md')]: {
      fontSize: 22,
    },
  },
}));

export { useButtonStyles };

export default useStyles;
